import React, { FC, useEffect, useRef } from "react";
import { defineMessages, useIntl } from "react-intl";

import { IconButton } from "@natera/material/lib/button";
import { routes } from "@app/routing";
import HomeSvg from "@assets/svg/icons/home.svg";
import HomeActiveSvg from "@assets/svg/icons/home_active.svg";
import BillingSvg from "@assets/svg/icons/billing.svg";
import BillingActiveSvg from "@assets/svg/icons/billing_active.svg";
import AccountSvg from "@assets/svg/icons/account.svg";
import AccountActiveSvg from "@assets/svg/icons/account_active.svg";
import PhoneSvg from "@assets/svg/icons/phone.svg";
import PhoneActiveSvg from "@assets/svg/icons/phone_active.svg";
import HistorySvg from "@assets/svg/icons/history.svg";
import HistoryActiveSvg from "@assets/svg/icons/history_active.svg";
import FeedbackSvg from "@assets/svg/icons/feedback.svg";
import FeedbackActiveSvg from "@assets/svg/icons/feedback_active.svg";
import ExitSvg from "@assets/svg/icons/exit.svg";
import ExitActiveSvg from "@assets/svg/icons/exit_active.svg";
import CloseSvg from "@assets/svg/icons/close.svg";
import NavItem from "./navItem";
import { Link } from "react-router-dom";
import Logo from "@assets/svg/natera-portal-logo.svg";
import Svg from "@natera/material/lib/svg";
import { ConfigContext, UserContext } from "@app/provider";
import { HeapEventLocation, HEAP_EVENTS } from "@app/provider/types";
import { useBillingAccess } from "@app/postBill/postBillWrapper";
import classNames from "classnames";
import { createFocusTrapHandler } from "@app/utils/focusTrap";
import LanguageSelector from "@app/components/languageSelector";
import "./navigation.scss";

const messages = defineMessages({
  navigationHome: {
    id: "navigationHome",
    defaultMessage: "Home",
  },
  navigationBillingCenter: {
    id: "navigationBillingCenter",
    defaultMessage: "Billing Center",
  },
  navigationMyAccount: {
    id: "navigationMyAccount",
    defaultMessage: "My Account",
  },
  navigationTestHistory: {
    id: "navigationTestHistory",
    defaultMessage: "Test History",
  },
  navigationPatientPortalFeedback: {
    id: "navigationPatientPortalFeedback",
    defaultMessage: "Share Feedback",
  },
  navigationPayMyBill: {
    id: "navigationPayMyBill",
    defaultMessage: "Pay My Bill",
  },
  navigationContactUs: {
    id: "navigationContactUs",
    defaultMessage: "Contact Us",
  },
  navigationSignOut: {
    id: "navigationSignOut",
    defaultMessage: "Log Out",
  },
  navigationNateraHomeLogo: {
    id: "navigationNateraHomeLogo",
    defaultMessage: "Natera home",
  },
  navigationNateraLabel: {
    id: "navigationNateraLabel",
    defaultMessage: "Menu",
  },
  navigationCloseMenu: {
    id: "navigationCloseMenu",
    defaultMessage: "Close menu",
  },
});

interface Props {
  isMenuOpen: boolean;
  toggleMenu: () => void;
  /**
   * Opt-in flag for the mobile drawer slide animation. Set to true when the
   * open/close was triggered by a hamburger click; false when it changed
   * because of a viewport resize. Drives the `.navigation--animate` CSS class
   * that carries the transform transition.
   */
  animateMenu?: boolean;
}

const TABLET_MIN_WIDTH = 746;

const Navigation: FC<Props> = ({ isMenuOpen, toggleMenu, animateMenu }) => {
  const intl = useIntl();
  const { config } = React.useContext(ConfigContext);
  const { uppUser } = React.useContext(UserContext);
  const { hasAccess, isLoading, hasError } = useBillingAccess();
  const navRef = useRef<HTMLElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [isDesktopViewport, setIsDesktopViewport] = React.useState(
    () => typeof window !== "undefined" && window.innerWidth >= TABLET_MIN_WIDTH
  );

  React.useEffect(() => {
    const desktopViewportQuery = window.matchMedia(
      `(min-width: ${TABLET_MIN_WIDTH}px)`
    );
    const handler = (e: MediaQueryListEvent) => setIsDesktopViewport(e.matches);
    setIsDesktopViewport(desktopViewportQuery.matches);
    desktopViewportQuery.addEventListener("change", handler);
    return () => desktopViewportQuery.removeEventListener("change", handler);
  }, []);

  const navigationMainItems = () => (
    <>
      <NavItem
        icon={HomeSvg}
        activeIcon={HomeActiveSvg}
        title={intl.formatMessage(messages.navigationHome)}
        to={routes.home}
        heapEventName={HEAP_EVENTS.upp_click_home}
      >
        {intl.formatMessage(messages.navigationHome)}
      </NavItem>

      {hasAccess && !hasError && !isLoading && (
        <NavItem
          icon={BillingSvg}
          activeIcon={BillingActiveSvg}
          title={intl.formatMessage(messages.navigationBillingCenter)}
          to={routes.billingCenter}
          heapEventName={HEAP_EVENTS.upp_click_billingcenter}
          heapEventProps={{
            location: HeapEventLocation.burgerMenu,
            identity: uppUser?.patientUID,
          }}
        >
          {intl.formatMessage(messages.navigationBillingCenter)}
        </NavItem>
      )}
    </>
  );

  useEffect(() => {
    if (!isMenuOpen) return;

    previousFocusRef.current = document.activeElement as HTMLElement;
    navRef.current?.focus();

    const handleKeyDown = createFocusTrapHandler(navRef.current, toggleMenu);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [isMenuOpen, toggleMenu]);

  return (
    <>
      <nav
        ref={navRef}
        className={classNames("navigation", {
          "navigation--closed": !isMenuOpen,
          "navigation--animate": animateMenu,
        })}
        {...(!isDesktopViewport && {
          role: "dialog",
          "aria-modal": true,
        })}
        aria-label={intl.formatMessage(messages.navigationNateraLabel)}
        tabIndex={-1}
      >
        <div className="navigation__logo-row">
          <div className="navigation__logo">
            <Link
              to="/"
              aria-label={intl.formatMessage(messages.navigationNateraHomeLogo)}
            >
              <Svg content={Logo} role="img" />
            </Link>
          </div>
          {isDesktopViewport ? (
            <div className="navigation__language">
              <LanguageSelector />
            </div>
          ) : (
            <div className="navigation__close">
              <IconButton
                onClick={toggleMenu}
                aria-label={intl.formatMessage(messages.navigationCloseMenu)}
              >
                {CloseSvg}
              </IconButton>
            </div>
          )}
        </div>

        <ul
          className="navigation__content navigation__main"
          role="presentation"
        >
          {navigationMainItems()}
        </ul>

        <ul
          className="navigation__content navigation__second"
          role="presentation"
        >
          <NavItem
            icon={HistorySvg}
            activeIcon={HistoryActiveSvg}
            title={intl.formatMessage(messages.navigationTestHistory)}
            to={routes.testHistory}
            heapEventName={HEAP_EVENTS.upp_click_testhistory}
          >
            {intl.formatMessage(messages.navigationTestHistory)}
          </NavItem>

          {(hasAccess === false || hasError) && !isLoading && (
            <NavItem
              icon={BillingSvg}
              activeIcon={BillingActiveSvg}
              title={intl.formatMessage(messages.navigationPayMyBill)}
              to={config.links.PAY_MY_BILL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {intl.formatMessage(messages.navigationPayMyBill)}
            </NavItem>
          )}

          <NavItem
            icon={FeedbackSvg}
            activeIcon={FeedbackActiveSvg}
            title={intl.formatMessage(messages.navigationPatientPortalFeedback)}
            to={routes.feedback}
            heapEventName={HEAP_EVENTS.upp_core_share_feedback_menu_click}
          >
            {intl.formatMessage(messages.navigationPatientPortalFeedback)}
          </NavItem>

          <NavItem
            icon={PhoneSvg}
            activeIcon={PhoneActiveSvg}
            title={intl.formatMessage(messages.navigationContactUs)}
            to={config.links.CONTACT_US}
            target="_blank"
            rel="noopener noreferrer"
            heapEventName={HEAP_EVENTS.upp_click_contactnatera}
            heapEventProps={{ location: HeapEventLocation.main_menu }}
          >
            {intl.formatMessage(messages.navigationContactUs)}
          </NavItem>
        </ul>

        <ul
          className="navigation__content navigation__bottom-routes"
          role="presentation"
        >
          <NavItem
            icon={AccountSvg}
            activeIcon={AccountActiveSvg}
            title={intl.formatMessage(messages.navigationMyAccount)}
            to={routes.profile}
            isMobileMenu={isMenuOpen}
            heapEventName={HEAP_EVENTS.upp_click_patientprofile}
            heapEventProps={{ location: HeapEventLocation.burgerMenu }}
          >
            {intl.formatMessage(messages.navigationMyAccount)}
          </NavItem>

          <NavItem
            icon={ExitSvg}
            activeIcon={ExitActiveSvg}
            title={intl.formatMessage(messages.navigationSignOut)}
            to={routes.logOut}
            className="logout-item"
            data-automation_id="log_out_button"
          >
            {intl.formatMessage(messages.navigationSignOut)}
          </NavItem>
        </ul>
      </nav>
      <div
        className={classNames("navigation__overlay", {
          "navigation__overlay--closed": !isMenuOpen,
        })}
        onClick={toggleMenu}
      />
    </>
  );
};

export default Navigation;
